import React, { useCallback, useEffect, useRef, useState } from 'react';

export default function WebcamCapture({ onCapture, disabled, className = '' }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const validationTimerRef = useRef(null);
  const previewUrlRef = useRef('');
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [guideState, setGuideState] = useState({ status: 'idle', message: 'Align face and shoulders' });
  const [canAutoValidate, setCanAutoValidate] = useState(true);
  const [previewUrl, setPreviewUrl] = useState('');

  const stopCamera = useCallback(() => {
    if (validationTimerRef.current) {
      clearInterval(validationTimerRef.current);
      validationTimerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setReady(false);
  }, []);

  const clearPreview = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = '';
    }
    setPreviewUrl('');
  }, []);

  const startCamera = useCallback(async () => {
    setError('');
    setValidationMessage('');
    clearPreview();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 960 },
          aspectRatio: { ideal: 4 / 3 }
        }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setReady(true);
      }
    } catch (err) {
      setError('Camera access denied or unavailable.');
    }
  }, [clearPreview]);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
      clearPreview();
    };
  }, [startCamera, stopCamera, clearPreview]);

  const checkFaceGeometry = useCallback((faces, width, height) => {
    if (!faces || faces.length === 0) {
      return { ok: false, message: 'No face detected' };
    }
    if (faces.length > 1) {
      return { ok: false, message: 'Only one person' };
    }
    const box = faces[0].boundingBox;
    const centerX = (box.x + box.width / 2) / width;
    const top = box.y / height;
    const bottom = (box.y + box.height) / height;
    const faceWidth = box.width / width;
    const faceHeight = box.height / height;

    if (centerX < 0.32 || centerX > 0.68) {
      return { ok: false, message: 'Center your face' };
    }
    if (top < 0.04 || top > 0.34) {
      return { ok: false, message: 'Place face in oval' };
    }
    if (faceWidth < 0.16 || faceHeight < 0.20) {
      return { ok: false, message: 'Move closer' };
    }
    if (faceWidth > 0.48 || faceHeight > 0.58 || bottom > 0.70) {
      return { ok: false, message: 'Move back for shoulders' };
    }
    return { ok: true, message: 'Ready to capture' };
  }, []);

  useEffect(() => {
    if (!ready) return undefined;
    if (typeof window === 'undefined' || typeof window.FaceDetector !== 'function') {
      setCanAutoValidate(false);
      setGuideState({ status: 'idle', message: 'Align in guide' });
      return undefined;
    }
    setCanAutoValidate(true);

    const detector = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 2 });
    const runCheck = async () => {
      const video = videoRef.current;
      if (!video || !video.videoWidth) return;
      try {
        const faces = await detector.detect(video);
        const result = checkFaceGeometry(faces, video.videoWidth, video.videoHeight);
        setGuideState({ status: result.ok ? 'good' : 'bad', message: result.message });
      } catch (_) {
        setGuideState({ status: 'bad', message: 'Face check unavailable' });
      }
    };

    runCheck();
    validationTimerRef.current = setInterval(runCheck, 900);
    return () => {
      if (validationTimerRef.current) clearInterval(validationTimerRef.current);
    };
  }, [ready, checkFaceGeometry]);

  const capture = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return null;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    // Correct orientation: mirror so saved photo matches what user sees (selfie view)
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.85);
    });
  }, []);

  const validateFaceAndShoulders = useCallback(async (blob) => {
    if (typeof window === 'undefined' || typeof window.FaceDetector !== 'function') {
      return { ok: true };
    }

    let bitmap;
    try {
      bitmap = await createImageBitmap(blob);
      const detector = new window.FaceDetector({ fastMode: false, maxDetectedFaces: 2 });
      const faces = await detector.detect(bitmap);
      const width = bitmap.width || videoRef.current?.videoWidth || 1;
      const height = bitmap.height || videoRef.current?.videoHeight || 1;
      bitmap.close?.();
      const result = checkFaceGeometry(faces, width, height);
      if (!result.ok) {
        const messageMap = {
          'No face detected': 'No face detected. Face the camera and capture again.',
          'Only one person': 'Only one person should be in the photo.',
          'Center your face': 'Center your face inside the oval guide.',
          'Place face in oval': 'Move the camera so your full face is inside the upper oval.',
          'Move closer': 'Move closer. Your face is too small in the photo.',
          'Move back for shoulders': 'Move the camera farther back so both shoulders are visible.'
        };
        return { ok: false, message: messageMap[result.message] || result.message };
      }
      return { ok: true };
    } catch (err) {
      bitmap?.close?.();
      return {
        ok: false,
        message: 'Could not validate the selfie. Retake with clear lighting and keep face and shoulders visible.'
      };
    }
  }, [checkFaceGeometry]);

  const handleCapture = useCallback(async () => {
    setValidationMessage('');
    const blob = await capture();
    if (!blob) return;
    const validation = await validateFaceAndShoulders(blob);
    if (!validation.ok) {
      setValidationMessage(validation.message);
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    clearPreview();
    previewUrlRef.current = objectUrl;
    setPreviewUrl(objectUrl);
    stopCamera();
    if (onCapture) onCapture(blob);
  }, [capture, clearPreview, onCapture, stopCamera, validateFaceAndShoulders]);

  const handleRetake = useCallback(() => {
    startCamera();
  }, [startCamera]);

  const guideColorClass =
    guideState.status === 'good'
      ? 'border-emerald-400'
      : guideState.status === 'bad'
        ? 'border-red-500'
        : 'border-white/90';
  const guideTextClass =
    guideState.status === 'good'
      ? 'bg-emerald-600/90'
      : guideState.status === 'bad'
        ? 'bg-red-600/90'
        : 'bg-black/55';

  return (
    <div className={className}>
      <div className="relative flex justify-center">
        <div className="relative w-full max-w-sm aspect-[4/3] overflow-hidden rounded-xl bg-gray-800 ring-4 ring-indigo-500/50 ring-offset-2 ring-offset-gray-100 shadow-lg">
          {previewUrl ? (
            <>
              <img src={previewUrl} alt="Captured attendance preview" className="h-full w-full object-cover" />
              <div className="absolute bottom-2 left-2 right-2 rounded bg-emerald-600/90 px-2 py-1 text-center text-[11px] font-medium text-white">
                Photo captured
              </div>
            </>
          ) : (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover scale-x-[-1]"
                aria-label="Camera preview - position face and shoulders in the guide"
              />
              <div className="pointer-events-none absolute inset-0">
                <div className={`absolute left-1/2 top-[12%] h-[45%] w-[38%] -translate-x-1/2 rounded-full border-4 shadow-[0_0_0_9999px_rgba(0,0,0,0.18)] transition-colors ${guideColorClass}`} />
                <div className={`absolute left-1/2 bottom-[13%] h-[27%] w-[68%] -translate-x-1/2 rounded-t-[999px] border-4 border-b-0 transition-colors ${guideColorClass}`} />
                <div className="absolute inset-x-0 top-1/2 border-t border-white/25" />
                <div className="absolute inset-y-0 left-1/2 border-l border-white/25" />
                <div className={`absolute bottom-2 left-2 right-2 rounded px-2 py-1 text-center text-[11px] font-medium text-white transition-colors ${guideTextClass}`}>
                  {guideState.message}
                </div>
              </div>
            </>
          )}
          {!previewUrl && !ready && !error && (
            <p className="absolute inset-0 flex items-center justify-center text-white text-sm">Starting camera...</p>
          )}
          {!previewUrl && error && (
            <p className="absolute inset-0 flex items-center justify-center text-red-400 text-sm p-2 text-center">{error}</p>
          )}
        </div>
      </div>
      <p className="text-center text-xs text-gray-500 mt-2">
        Hold the phone at chest height and keep your full face and both shoulders visible.
        {!canAutoValidate ? ' Auto face check is unavailable on this browser, so use the guide before capture.' : ''}
      </p>
      {validationMessage ? (
        <p className="mt-2 rounded border border-red-200 bg-red-50 p-2 text-center text-xs text-red-700">{validationMessage}</p>
      ) : null}
      {previewUrl ? (
        <button type="button" onClick={handleRetake} disabled={disabled} className="mt-2 w-full bg-gray-700 text-white py-2 rounded text-sm disabled:opacity-50" aria-label="Retake photo">
          Retake photo
        </button>
      ) : (
        <button type="button" onClick={handleCapture} disabled={disabled || !ready || !!error} className="mt-2 w-full bg-indigo-600 text-white py-2 rounded text-sm disabled:opacity-50" aria-label="Capture photo">
          Capture photo
        </button>
      )}
    </div>
  );
}
